import { useState } from "react";
import { Col, Button } from "react-bootstrap";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { Breakpoint } from "react-socks";

import "./WidgetBuilder.scss";
import { ReactComponent as BoltIcon } from "assets/images/icons/Bolt.svg";
import { ReactComponent as CalculatorIcon } from "assets/images/icons/Calculator.svg";
import { ReactComponent as ChartIcon } from "assets/images/icons/Chart.svg";
import { ReactComponent as FileInvoiceDollarIcon } from "assets/images/icons/FileInvoiceDollar.svg";
import { ReactComponent as ShipIcon } from "assets/images/icons/Ship.svg";
import { ReactComponent as TasksIcon } from "assets/images/icons/Tasks.svg";

import usePermissions from "hooks/usePermissions";
import Card from "components/molecules/Card/Card";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import TasksWidget from "../../widgets/TasksWidget";
import RecentDeliveriesWidget from "../../../shipper/widgets/RecentDeliveriesWidget";
import RecentUpdatesWidget from "../../../shipper/widgets/RecentUpdatesWidget";
import ExceptionsWidget from "../../../shipper/widgets/ExceptionsWidget";
import RecentlyVisitedWidget from "../../../shipper/widgets/RecentlyVisitedWidget";

const ListWidgetBuilder = function ({
  widgetDataSource,
  widgetContent,
  handleWidgetContentClick,
  handleWidgetDataSourceClick,
  setWidgetFilters,
  widgetFilters,
}) {
  const [activeKey, setActiveKey] = useState("");
  const intl = useIntl();
  const methods = useForm();
  const { permissions: permissionsUnparsed } = usePermissions();

  const permissions = permissionsUnparsed ? JSON.parse(permissionsUnparsed) : null;
  const paymentLocations = permissions?.payment_location?.locations.sort((a, b) => a.label.localeCompare(b.label));
  paymentLocations?.unshift({
    label: intl.formatMessage({
      id: "general.all",
      defaultMessage: "All",
    }),
    value: "all",
  });

  const renderPreview = () => {
    const paymentLocation = widgetFilters?.payment_location === "all" ? null : widgetFilters?.payment_location;
    switch (widgetContent) {
      case "Exceptions":
        return <ExceptionsWidget isPreview isLayoutLocked paymentLocation={paymentLocation} />;
      case "Recent updates":
        return (
          <RecentUpdatesWidget
            isPreview
            isLayoutLocked
            paymentLocation={paymentLocation}
            widgetDataSource={widgetDataSource}
          />
        );
      case "Recent deliveries":
        return <RecentDeliveriesWidget isPreview isLayoutLocked paymentLocation={paymentLocation} />;
      case "Tasks":
        return <TasksWidget isPreview isLayoutLocked paymentLocation={paymentLocation} />;
      case "Recently visited":
        return <RecentlyVisitedWidget isPreview isLayoutLocked />;
      default:
        return null;
    }
  };

  const dataSourceButtons = [
    ...(permissions?.menu_items?.view_quick_requests
      ? [
          {
            name: "quick_request",
            id: "general.quickRequests",
            defaultMessage: "Quick requests",
            label: "Quick requests",
            icon: <BoltIcon />,
            content: [
              {
                name: "Recent updates",
                id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_quote_requests
      ? [
          {
            name: "quote_request",
            id: "general.quoteRequests",
            defaultMessage: "Quote requests",
            label: "Quote requests",
            icon: <CalculatorIcon />,
            content: [
              {
                name: "Recent updates",
                id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_shipments
      ? [
          {
            name: "shipment",
            id: "general.shipments",
            defaultMessage: "Shipments",
            label: "Shipments",
            icon: <ShipIcon />,
            content: [
              ...(permissions.user_type === "shipper"
                ? [
                    {
                      name: "Exceptions",
                      id: "general.exceptions",
                    },
                  ]
                : []),
              {
                name: "Recent deliveries",
                id: "dashboard.widgetDialog.listWidgetBuilder.recentDeliveries",
              },
              {
                name: "Recent updates",
                id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_invoices
      ? [
          {
            name: "invoice",
            id: "general.invoices",
            defaultMessage: "Invoices",
            label: "Invoices",
            icon: <FileInvoiceDollarIcon />,
            content: [
              {
                name: "Recent updates",
                id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
              },
            ],
          },
        ]
      : []),
    ...(permissions?.menu_items?.view_analytics
      ? [
          {
            name: "tasks",
            id: "general.resolutionCenter",
            defaultMessage: "Resolution Center",
            label: "Resolution Center",
            icon: <TasksIcon />,
            content: [
              {
                name: "Tasks",
                id: "general.resolutionCenter",
              },
            ],
          },
        ]
      : []),
    {
      name: "all",
      id: "general.all",
      defaultMessage: "All",
      label: "All",
      icon: <ChartIcon />,
      content: [
        {
          name: "Recent updates",
          id: "dashboard.widgetDialog.listWidgetBuilder.recentUpdate",
        },
        {
          name: "Recently visited",
          id: "dashboard.widgetDialog.listWidgetBuilder.recentlyVisited",
        },
      ],
    },
  ];

  const handlePaymentlocation = (item) => {
    if (item[0]?.value === "all") {
      const { payment_location, ...newWidgetFilters } = widgetFilters;
      setWidgetFilters(newWidgetFilters);
    } else {
      setWidgetFilters({ ...widgetFilters, payment_location: item[0]?.value });
    }
  };

  return (
    <div className="d-flex flex-column flex-md-row">
      <FormProvider {...methods}>
        <Col className="choose-widget-column">
          <p className="choose-widget-title">
            <FormattedMessage id="general.chooseDataSource" defaultMessage="Choose data source" />
          </p>

          <div>
            <Breakpoint large up medium >
              {dataSourceButtons.map((button) => (
                <Button
                  className={`mb-3 d-flex flex-row align-items-center ${
                    widgetDataSource !== button.name && "list-widget-button"
                  }`}
                  onClick={() => handleWidgetDataSourceClick(button.name)}
                  style={{
                    justifyContent: "flex-start !important",
                    borderRadius: "26px",
                    border: "none",
                    backgroundColor: widgetDataSource === button.name ? "#000000" : "rgba(0, 0, 0, 0)",
                    color: widgetDataSource === button.name ? "#FFFFFF" : "#000000",
                    padding: "7px 0px",
                    width: "150px",
                    height: "32px",
                  }}
                >
                  <div
                    className=" ml-3"
                    style={{
                      filter: widgetDataSource === button.name ? "invert(100%) " : "none",
                    }}
                  >
                    {button.icon}
                  </div>
                  <span className="widget-button-text">
                    <FormattedMessage id={button.id} defaultMessage={button.defaultMessage} />
                  </span>
                </Button>
              ))}
            </Breakpoint>
          </div>
          <div>
            <Breakpoint small down>
              <Dropdown
                value={activeKey}
                options={dataSourceButtons}
                onChange={(val) => {
                  setActiveKey(val.label);
                  handleWidgetDataSourceClick(val.name);
                }}
              />
            </Breakpoint>
          </div>
        </Col>

        <Col className="choose-widget-column mt-3 mt-md-0">
          <p className={`choose-widget-title ${!widgetDataSource && "text-secondary"}`}>
            <FormattedMessage id="general.chooseWidgetContent" defaultMessage="Choose widget content" />
          </p>
          {dataSourceButtons
            .filter((button) => button.name === widgetDataSource)
            .map((button) => (
              <div>
                {button.content.map((content) => (
                  <Button
                    className={`mb-3 d-flex flex-row align-items-center justify-content-center ${
                      widgetContent !== content.name && "list-widget-button"
                    }`}
                    onClick={() => handleWidgetContentClick(content.name)}
                    style={{
                      borderRadius: "26px",
                      padding: "7px 0px",
                      width: "145px",
                      height: "32px",
                      border: "1px solid #000000",
                      color: widgetContent === content.name ? "#FFFFFF" : "#000000",
                      backgroundColor: widgetContent === content.name ? "#000000" : "rgba(0, 0, 0, 0)",
                    }}
                  >
                    <span className="widget-button-text">
                      <FormattedMessage id={content.id} defaultMessage={content.name} />
                    </span>
                  </Button>
                ))}
              </div>
            ))}
        </Col>

        {widgetContent !== "Recently visited" && paymentLocations?.length > 0 && (
          <Col className="choose-widget-column">
            <p className={`choose-widget-title ${!widgetContent && "text-secondary"}`}>
              <FormattedMessage id="general.filterContentBy" defaultMessage="Filter content by" />
            </p>
            {widgetContent && (
              <Col className="choose-widget-column">
                <div className="w-136-px">
                  <SearchSelect
                    defaultValue={widgetFilters?.payment_location || "all"}
                    options={paymentLocations}
                    name="payment_location"
                    onSelect={handlePaymentlocation}
                  />
                </div>
              </Col>
            )}
          </Col>
        )}
        <Col className="preview-widget-column mt-3 mt-md-0">
          <p className={`choose-widget-title ${!widgetContent && "text-secondary"}`}>
            <FormattedMessage id="general.preview" defaultMessage="Preview" />
          </p>
          {widgetContent && (
            <div style={{ border: "16px solid #F1F3F4", borderRadius: "24px" }} className="preview-card">
              <Card className="flex-grow-0 p-0">{renderPreview()}</Card>
            </div>
          )}
        </Col>
      </FormProvider>
    </div>
  );
};

ListWidgetBuilder.propTypes = {
  widgetDataSource: PropTypes.string.isRequired,
  widgetContent: PropTypes.string.isRequired,
  handleWidgetContentClick: PropTypes.func.isRequired,
  handleWidgetDataSourceClick: PropTypes.func.isRequired,
  setWidgetFilters: PropTypes.func.isRequired,
  widgetFilters: PropTypes.instanceOf(Object).isRequired,
};

export default ListWidgetBuilder;
